import { ConfirmModal, PageWrapper, StickyActionsHeader } from "@/components";
import HistoryLogTable from "@/components/History/HistoryLogTable";
import * as GlobalS from "@/components/styled";
import { CONTENT_WIDTH, DATE_FORMATS, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import { useToggle } from "@/hooks";
import {
  useDeleteBaselineDateMutation,
  useGetBaselineDateQuery,
  useGetBaselineDatesQuery,
  useGetFormLookupsQuery,
  useGetHistoryLogsByIdQuery,
  useSaveBaselineDateMutation,
} from "@/services";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Calendar,
  Card,
  Col,
  Form,
  Popconfirm,
  Row,
  Select,
  Space,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { func, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EditBaselineDateActions from "./EditBaselineDateActions";

const { Text } = Typography;

const EditBaselineDate = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { projectTypeId, baselineDateId } = useParams();
  const [form] = Form.useForm();

  const [selectedDates, setSelectedDates] = useState([]);
  const [addDate, setAddDate] = useState(null);
  const [projectType, setProjectType] = useState();
  const [invalidProductCategories, setInvalidProductCategories] = useState();
  const [showConfirmDelete, toggleShowConfirmDelete] = useToggle(false);

  const [deleteBaselineDate, { isLoading: isDeleting }] =
    useDeleteBaselineDateMutation();
  const { data: baselineDate, isFetching } = useGetBaselineDateQuery(
    {
      projectTypeId,
      baselineDateId,
    },
    {
      skip: !projectTypeId || !baselineDateId,
    }
  );
  const { data: baselineDates } = useGetBaselineDatesQuery();
  const [saveBaselineDate, { isLoading: isSaving }] =
    useSaveBaselineDateMutation();
  const { refetch: refetchHistory } = useGetHistoryLogsByIdQuery({
    parentEntityId: baselineDateId,
  });

  useEffect(() => {
    if (!baselineDates) return;

    const disabledProductCategories = {};

    baselineDates.forEach(({ id, productCategories, projectTypeId }) => {
      if (id === baselineDateId) return;

      const productCategoriesMap = Object.fromEntries(
        (productCategories || []).map((k) => [k, true])
      );
      disabledProductCategories[projectTypeId] = {
        ...disabledProductCategories[projectTypeId],
        ...productCategoriesMap,
      };
    });

    setInvalidProductCategories(disabledProductCategories);
  }, [baselineDateId, baselineDates]);

  const [projectTypes, setProjectTypes] = useState(null);
  const [productCategories, setProductCategories] = useState(null);

  const { data: forms } = useGetFormLookupsQuery();

  useEffect(() => {
    setProjectTypes(forms?.intake?.projectType);
    setProductCategories(forms?.intake?.productCategory);
  }, [forms?.intake?.productCategory, forms?.intake?.projectType]);

  useEffect(() => {
    if (!projectTypeId || !baselineDate) return;

    form.setFieldsValue({
      productCategories: baselineDate.productCategories,
      projectTypeId,
    });

    setSelectedDates(baselineDate.dates);
    setProjectType(projectTypeId);
  }, [baselineDate, form, projectTypeId]);

  const handleSave = (data) => {
    const request = {
      dates: selectedDates,
      ...data,
      id: baselineDateId,
    };
    saveBaselineDate({
      projectTypeId: data.projectTypeId,
      baselineDate: request,
    })
      .unwrap()
      .then(() => {
        refetchHistory();
        toast.success(intl.formatMessage({ id: `baselineDates.saveSuccess` }));
        navigate(routes.admin.baselineDates.list);
      })
      .catch(() =>
        toast.error(intl.formatMessage({ id: `baselineDates.saveError` }))
      );
  };

  const handleAddDate = () => {
    setSelectedDates((prev) => {
      const dates = [...(prev || []), addDate];
      return [...new Set(dates)].sort((a, b) =>
        dayjs(a, DATE_FORMATS.fourDigitYear).diff(
          dayjs(b, DATE_FORMATS.fourDigitYear)
        )
      );
    });
  };
  const handleDateChange = (date) => {
    setAddDate(date.format(DATE_FORMATS.fourDigitYear));
  };

  const handleDeleteDate = (date) => {
    setSelectedDates((previousDates) => [
      ...(previousDates?.filter((previousDate) => previousDate !== date) || []),
    ]);
  };

  const handleSelectProjectType = (selectedProjectType) => {
    setProjectType(selectedProjectType);
    form.setFieldValue("ProductCategories", []);
  };

  const handleDelete = () => {
    deleteBaselineDate({
      projectTypeId,
      id: baselineDateId,
    })
      .unwrap()
      .then(() => {
        toast.success(
          intl.formatMessage({ id: "baselineDates.deleteSuccess" })
        );
        navigate(routes.admin.baselineDates.list);
      })
      .catch(() =>
        toast.error(intl.formatMessage({ id: "baselineDates.deleteError" }))
      );
  };

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: `baselineDates.title` })}
      backButtonRoute={routes.admin.baselineDates.list}
      pageActions={
        <StickyActionsHeader
          rightChildren={
            <EditBaselineDateActions
              pageName="edit-project-template"
              saveOnClick={form.submit}
              cancelOnClick={() => navigate(routes.admin.baselineDates.list)}
              loading={isSaving}
              saveDisabled={!selectedDates}
              deleteOnClick={toggleShowConfirmDelete}
              showDelete={!!baselineDateId}
            />
          }
        />
      }
    >
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.half}>
          <GlobalS.PageContentCard
            title={intl.formatMessage({ id: "baselineDates.editTitle" })}
          >
            <Form form={form} layout="vertical" onFinish={handleSave}>
              <Row gutter={[16, 0]}>
                <Col>
                  <Form.Item
                    name="projectTypeId"
                    label="Project Type"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "baselineDates.requiredProjectType",
                        }),
                      },
                    ]}
                  >
                    <Select
                      data-test-id="project-type-select"
                      onChange={handleSelectProjectType}
                      disabled={!!baselineDateId && !!projectTypeId}
                    >
                      {projectTypes &&
                        Object.keys(projectTypes).map((key) => (
                          <Select.Option key={key} value={key}>
                            {projectTypes[key]?.fallback}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Item
                    name="productCategories"
                    label="Product Category"
                    rules={[
                      {
                        required: true,
                        message: intl.formatMessage({
                          id: "baselineDates.requiredProductCategory",
                        }),
                      },
                    ]}
                  >
                    <Select mode="multiple" allowClear>
                      {!!productCategories &&
                        Object.keys(productCategories).map((key) => (
                          <Select.Option
                            key={key}
                            value={key}
                            disabled={
                              invalidProductCategories?.[projectType]?.[key]
                            }
                          >
                            {productCategories[key]?.fallback}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]} wrap={false}>
                <Col>
                  <GlobalS.SmallCard>
                    <Row>
                      <Calendar
                        fullscreen={false}
                        onChange={handleDateChange}
                        data-test-id="calendar-baseline-dates"
                      />
                    </Row>
                    <Row justify="end">
                      {addDate && (
                        <Button
                          data-test-id="add-date-btn"
                          type="primary"
                          onClick={handleAddDate}
                          disabled={!addDate}
                        >
                          {intl.formatMessage({ id: "general.add" })}
                        </Button>
                      )}
                    </Row>
                  </GlobalS.SmallCard>
                </Col>
                <Col flex="auto">
                  <GlobalS.FullHeightCard
                    title={intl.formatMessage({
                      id: "baselineDates.selectedDates",
                    })}
                  >
                    <Row gutter={[24, 16]}>
                      {selectedDates?.map((date, index) => (
                        <DateDisplay
                          key={index}
                          date={date}
                          onDelete={handleDeleteDate}
                        />
                      ))}
                    </Row>
                  </GlobalS.FullHeightCard>
                </Col>
              </Row>
            </Form>
          </GlobalS.PageContentCard>
        </Col>
        <Col span={CONTENT_WIDTH.half}>
          <Card loading={isFetching}>
            <HistoryLogTable parentEntityId={baselineDateId} />
          </Card>
        </Col>
      </Row>
      <ConfirmModal
        loading={isDeleting}
        onSaveChanges={handleDelete}
        hidden={!showConfirmDelete}
        toggle={toggleShowConfirmDelete}
        headerTitleKey={"baselineDates.deleteModalTitle"}
        bodyTextKey={"baselineDates.deleteModalDescription"}
      />
    </PageWrapper>
  );
};

const DateDisplay = ({ date, onDelete }) => {
  const intl = useIntl();
  const handleDelete = () => onDelete(date);

  return (
    <Col key={date.toString()}>
      <Space>
        <Text>{date}</Text>
        <Popconfirm
          title={intl.formatMessage({
            id: "baselineDates.actionDelete",
          })}
          okText={intl.formatMessage({ id: "general.delete" })}
          cancelText={intl.formatMessage({ id: "general.cancel" })}
          onConfirm={() => handleDelete(date)}
          data-test-id="date-delete-confirm"
          placement="topRight"
        >
          <Button
            type="link"
            icon={<FontAwesomeIcon icon={faTrash} />}
            data-test-id="date-delete-btn"
          />
        </Popconfirm>
      </Space>
    </Col>
  );
};

DateDisplay.propTypes = {
  date: string,
  onDelete: func,
};

export default EditBaselineDate;
